import React, { createContext, useContext, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { AuthService } from '@public/auth/services';
import { apiClient } from '@shared/utils/api/client';
import APLoading from '@shared/components/molecules/loading';

import type { IAuthState } from './types';

import { AUTH_CLIENT_CONFIG, AuthClient } from './auth-client';
import { AuthInitializationErrorView, AuthStateBridge } from './components';
import { AuthInitializationError } from './errors';

const AuthCtx = createContext<AuthClient | null>(null);

const DEFAULT_AUTH_STATE: IAuthState = { status: 'idle', user: null };

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const clientRef = useRef<AuthClient | null>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [initError, setInitError] = React.useState<AuthInitializationError | null>(null);

  const client = clientRef.current;

  // getSnapshot reads directly from clientRef.current on every call so
  // useSyncExternalStore can detect state changes inside the AuthClient
  // instance. Using useMemo would cache the snapshot object reference and
  // React would never see the mutation.
  const getSnapshot = (): IAuthState => clientRef.current?.snapshot ?? DEFAULT_AUTH_STATE;

  const subscribe = (onChange: () => void) => {
    if (!client) return () => {};
    const subscription = client.on('auth:change', onChange);
    // Convert EventSubscription to a function
    return () => {
      if ('unsubscribe' in subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  };

  const handleAuth = async () => {
    try {
      const authClient = await AuthClient.create(AUTH_CLIENT_CONFIG);
      clientRef.current = authClient;
      authClient.clearAuthState();
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize AuthClient:', error);
      // Check if it's our custom error
      if (error instanceof AuthInitializationError) {
        setInitError(error);
      } else {
        // Wrap unknown errors in our custom error
        setInitError(new AuthInitializationError('errors.auth.init.unknown', error, 'unknown'));
      }
      setIsInitialized(false);
    }
  };

  const initializeAuth = React.useCallback(async () => {
    try {
      setInitError(null);

      const sessionData = await AuthService.getCurrentSession();

      if (sessionData.authenticated) {
        const authClient = await AuthClient.createWithoutInit(AUTH_CLIENT_CONFIG, sessionData);
        clientRef.current = authClient;
        setIsInitialized(true);
        return;
      }

      // Session exists but not authenticated - proceed with login flow
      await handleAuth();
    } catch {
      await handleAuth();
    }
  }, []);
  // Listen for unauthorized events from ApiClient
  // Only clear auth state - let router guards handle the redirect to login
  // This prevents race conditions where the redirect happens before
  // hydrateFromSession() can handle the 401 error gracefully
  const handleUnauthorized = () => {
    if (!clientRef.current) return;
    clientRef.current.clearAuthState();
  };

  const queryClient = useQueryClient();

  // Keep apiClient in sync with the authenticated user identity.
  // When the user logs out (status → unauthenticated), clear the React Query
  // cache so stale data from the previous session is never shown on re-login.
  useEffect(() => {
    if (!client) return;
    // Set initial identity from current snapshot
    const currentUser = client.snapshot?.user;
    apiClient.setUserIdentity(currentUser?.email ?? null);

    // Update identity whenever auth state changes
    const subscription = client.on('auth:change', () => {
      const user = client.snapshot?.user;
      apiClient.setUserIdentity(user?.email ?? null);

      // Clear React Query cache on logout so the next user starts fresh
      if (client.snapshot?.status === 'unauthenticated') {
        queryClient.clear();
      }
    });
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
      apiClient.setUserIdentity(null);
    };
  }, [client, queryClient]);

  // Wire the token-refresh handler so apiClient retries once on 401.
  useEffect(() => {
    if (!client) return;
    apiClient.setRefreshHandler(async () => {
      const refreshed = await AuthService.refresh();
      if (!refreshed) {
        client.clearAuthState();
      }
      return refreshed;
    });
    return () => {
      apiClient.setRefreshHandler(undefined);
    };
  }, [client]);

  useEffect(() => {
    const runInit = async () => {
      await initializeAuth();
    };

    runInit();

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      // Cleanup: stop session monitoring when component unmounts
      if (clientRef.current) {
        clientRef.current.destroy();
      }
    };
  }, [initializeAuth]);

  // Show error state if initialization failed
  if (initError) {
    return <AuthInitializationErrorView error={initError} onRetry={initializeAuth} />;
  }

  // Show loading state while initializing
  if (!isInitialized || !client) {
    return <APLoading />;
  }

  return (
    <AuthCtx.Provider value={client}>
      <AuthStateBridge subscribe={subscribe} getSnapshot={getSnapshot} />
      {children}
    </AuthCtx.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthClient = (): AuthClient => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuthClient must be used within <AuthProvider>');
  return ctx;
};

export default AuthProvider;
